/* eslint-disable react-hooks/exhaustive-deps */

import { useState, useEffect, useRef, DependencyList } from 'react';

const dependenciesChanged = (
  previous: DependencyList,
  current: DependencyList
) =>
  previous.length !== current.length ||
  current.some((dependency, i) => !Object.is(dependency, previous[i]));

export function useFetch<T>(
  fetchMethod: (...args: any[]) => Promise<T>,
  initialState: T,
  // Beholdt for bakoverkompatibilitet: første henting skjer alltid ved mount
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _executeOnInit: boolean = true,
  dependencies: DependencyList = [] as DependencyList
): [T, boolean, any, () => Promise<void>] {
  // Henter alltid ved første render, og igjen hver gang dependencies endres,
  // så isLoading starter som true i stedet for å settes i en effekt.
  const [data, setData] = useState<T>(initialState);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState(() => {});
  const [previousDependencies, setPreviousDependencies] =
    useState<DependencyList>(dependencies);
  // Brukes til å ignorere svar fra utdaterte kall og kall etter unmount
  const latestRequest = useRef(0);

  // Justerer state under render (i stedet for i en effekt) når dependencies endres
  if (dependenciesChanged(previousDependencies, dependencies)) {
    setPreviousDependencies(dependencies);
    setIsLoading(true);
    setError(undefined);
  }

  // Bruker promise-callbacks slik at state aldri settes synkront fra effekten
  const settle = (): Promise<void> => {
    const request = ++latestRequest.current;
    const isLatest = () => request === latestRequest.current;
    return new Promise<T>((resolve) => resolve(fetchMethod())).then(
      (result) => {
        if (isLatest()) {
          setData(result);
          setError(undefined);
          setIsLoading(false);
        }
      },
      (e) => {
        if (isLatest()) {
          setError(e);
          setIsLoading(false);
        }
      }
    );
  };

  const fetchData = async () => {
    setIsLoading(true);
    setError(undefined);
    await settle();
  };

  useEffect(() => {
    settle();
    return () => {
      latestRequest.current++;
    };
  }, [...dependencies]);

  return [data, isLoading, error, fetchData];
}
